import { useEffect, useLayoutEffect, useRef, useMemo, useState } from "react"
import { pick, omit, omitBy, isNil, isEqual, debounce, pickBy } from "lodash"
import { useSaveContextPreferencesDual, useSaveContextPreferencesKeepaliveDual, useReadContextPreference } from "hooks/preferences"
import { OK, ERROR } from "helpers/data-statuses"

/**
 * Single hook that handles both loading and saving preferences
 * Replaces the need for separate PreferenceSaver and PreferenceLoader components
 *
 * @param {string} subType - The preference subtype (e.g., "dashboard", "asset-view")
 * @param {ReadonlyArray<string>} preferenceFields - Array of field names to save as preferences
 * @param {object} state - Current state to save preferences from
 * @param {function} dispatch - Dispatch function to update state with loaded preferences
 * @param {function} setPreferencesAction - Action creator for setting preferences (e.g., setPreferences)
 * @param {object} [options] - Optional configuration
 * @param {object} [options.setPreferenceValidationOptions] - Function that receives preferences to validate
 *  and a callback to set the validation result. The callback will receive the validation result with the preferences
 *  and isCorrected boolean indicating if the preferences were corrected.
 * @param {number} [options.debounceTime=300] - Debounce time for saving (default 300ms)
 * @param {string} [options.fallbackSubType] - When set, if the primary key has no saved preferences, load from this
 *  key instead (e.g. legacy static key for panel preference migration).
 * @param {string[]} [options.omitLoadedKeys] - Preference keys to drop before dispatching loaded data (applies to
 *  primary and fallback). Prefer `omitFallbackKeys` when only legacy/fallback merge should ignore a key.
 * @param {string[]} [options.omitFallbackKeys] - When primary has no data and fallback is used, drop these keys from
 *  the merged payload (e.g. omit `view` so legacy migration does not override the panel default view).
 * @param {string} [options.mirrorSaveSubType] - When set and different from `subType`, saves also write to this key
 *  (e.g. per-panel key while primary reads/writes legacy).
 * @param {boolean} [options.disabled=false] - When true, fully disables preferences: no read request, no load
 *  dispatch, and no saves. `isPreferencesLoaded` resolves true immediately.
 * @param {boolean} [options.skipInitialLoad=false] - Skip the one-shot load dispatch while still reporting loaded and
 *  saving on change. Set when the caller's store slot is already authoritative at mount (e.g. a `key`-remount that
 *  returns to a dashboard whose RTK slice still holds live, possibly-unsaved state) so the cached read does not
 *  clobber it.
 * @param {boolean} [options.skipSave=false] - Suppress ONLY the per-subtype preference WRITE paths (on-change +
 *  validation-corrected saves); load/seed and `isPreferencesLoaded` are unaffected. Set in instance mode, where the
 *  workspace writer owns persistence. Unlike `disabled`, this does NOT touch the read path.
 * @returns {boolean} isPreferencesLoaded - Whether preferences have been loaded
 *
 * Hide-flush: a change still sitting in the trailing-only debounce below is otherwise lost if the
 * page refreshes/closes within the debounce window. On `visibilitychange -> hidden` we send it
 * immediately via a keepalive request; on unmount (in-app navigation) we flush it via the normal
 * request. Mirrors the instance-mode workspace writer (useWorkspaceSync, APP-2705).
 */
export function usePreferences(
  subType,
  preferenceFields,
  state,
  dispatch,
  setPreferencesAction,
  {
    setPreferenceValidationOptions,
    debounceTime = 300,
    fallbackSubType,
    omitLoadedKeys,
    omitFallbackKeys,
    mirrorSaveSubType,
    disabled = false,
    skipInitialLoad = false,
    skipSave = false,
  } = {},
) {
  const savePreferences = useSaveContextPreferencesDual(subType, mirrorSaveSubType)
  const savePreferencesKeepalive = useSaveContextPreferencesKeepaliveDual(subType, mirrorSaveSubType)
  // When disabled, skip the read entirely (not just the load/save effects) so nothing hits the
  // preferences API at all.
  const [preferences, preferenceStatus] = useReadContextPreference(subType, undefined, { enabled: !disabled })
  const fallbackEnabled = !!fallbackSubType && fallbackSubType !== subType
  const [fallbackPreferences, fallbackStatus] = useReadContextPreference(
    fallbackSubType ?? subType,
    undefined,
    { enabled: fallbackEnabled && !disabled },
  )
  const [validationResult, setValidationResult] = useState(null)

  const hasPrimaryFinished = preferenceStatus === OK || preferenceStatus === ERROR
  const hasFallbackFinished = !fallbackEnabled || fallbackStatus === OK || fallbackStatus === ERROR
  const hasPreferenceRequestFinished = hasPrimaryFinished && hasFallbackFinished

  // Derived: loaded when disabled (no prefs needed); when skipInitialLoad (the slot is already
  // authoritative and we deliberately neither load nor validate — so report loaded immediately,
  // otherwise the validation-gated branch below never flips and the save effect stays disabled,
  // silently stopping edits from persisting after a remount); when the async request finished and
  // no validation is required; or when validation has produced a result to apply.
  const isPreferencesLoaded = disabled
    || skipInitialLoad
    || (!setPreferenceValidationOptions && hasPreferenceRequestFinished)
    || (!!setPreferenceValidationOptions && validationResult !== null)

  // ===== LOADING LOGIC (runs once on mount) =====
  // Pre-latch when skipInitialLoad so the load effect early-returns: the slot is already
  // authoritative, so dispatching the cached read would overwrite live (possibly unsaved) state.
  const isInitialLoadRef = useRef(skipInitialLoad)
  const lastValidationResultRef = useRef(null)
  // Tags which subType a validationResult was actually requested for (set where
  // setPreferenceValidationOptions is called below). A subType switch doesn't clear
  // `validationResult` state itself, so without this tag, the validation-apply effect would treat
  // the OLD subtype's already-applied result as freshly-arrived the moment lastValidationResultRef
  // gets reset on switch — reprocessing stale data (dispatch + a debounced save) under the new
  // subtype. Comparing against the CURRENT subType directly (rather than a "just switched" flag)
  // works regardless of which render the validation-apply effect happens to fire on.
  const validationResultSubTypeRef = useRef(subType)

  // ===== SAVING LOGIC (runs on state changes) =====
  const previousPreferencesRef = useRef(null)
  const isInitialSaveRef = useRef(true)
  // Holds whatever payload is currently queued in the debounce below, or null once it's been sent
  // (by timer, flush, or hide-flush). Lets the hide-flush handler know there's something to send,
  // and what, without relying on lodash debounce's lack of a .pending() method in this version.
  const pendingSaveRef = useRef(null)

  // Refs so async paths below (the debounce callback itself, hide-flush, unmount cleanup) always
  // read the LATEST disabled/skipSave rather than whatever they closed over the last time their
  // owning effect ran. Ref writes happen during render (synchronous with commit), so unlike the
  // effects themselves — which React defers until after paint — there's no window where a mode
  // flip can race one of these callbacks.
  const disabledRef = useRef(disabled)
  disabledRef.current = disabled
  const skipSaveRef = useRef(skipSave)
  skipSaveRef.current = skipSave

  // savePreferences's identity churns whenever subType/mirrorSaveSubType change (its useCallback
  // deps include primarySubType, in useSaveContextPreferencesDual) — read it via a ref so
  // debouncedSave itself, and the pending timer it owns, survive a subType switch. If debouncedSave
  // depended on savePreferences directly, useMemo would swap in a brand-new (empty) debounce
  // instance the same render the subType-switch effect below tries to cancel the OLD one — leaving
  // that cancel() a no-op, and the real pending timer only reachable via the unmount-identity-change
  // cleanup further down, which flushes (not cancels) in the common case, resending a payload the
  // switch was supposed to drop.
  const savePreferencesRef = useRef(savePreferences)
  savePreferencesRef.current = savePreferences

  // Create a debounced save function to avoid rapid successive saves. The disabled/skipSave check
  // here is a backstop, not the primary guard (the effects below cancel this debounce as soon as
  // either flips true) — it exists so the write stays suppressed even if some future scheduling
  // site forgets to cancel on flip, rather than relying on every call site getting that right.
  const debouncedSave = useMemo(
    () => debounce((preferencesToSave) => {
      pendingSaveRef.current = null
      if (disabledRef.current || skipSaveRef.current) {
        return
      }
      savePreferencesRef.current(preferencesToSave)
    }, debounceTime),
    [debounceTime],
  )

  const prevSubTypeRef = useRef(subType)
  useLayoutEffect(() => {
    if (prevSubTypeRef.current === subType) {
      return
    }
    prevSubTypeRef.current = subType
    isInitialLoadRef.current = false
    isInitialSaveRef.current = true
    lastValidationResultRef.current = null
    // A payload still queued from the OLD subtype must not survive the switch: debouncedSave now
    // has a stable identity across subType changes (see savePreferencesRef above), so this cancels
    // the actual pending timer rather than a freshly-memoized, already-empty instance.
    pendingSaveRef.current = null
    debouncedSave.cancel()
  }, [subType, debouncedSave])

  // useLayoutEffect ensures the preference dispatch fires in the same commit phase as the
  // render where isPreferencesLoaded becomes true, preventing a one-frame window where
  // consumers see isPreferencesLoaded=true but Redux state still has defaults.
  useLayoutEffect(() => {
    if (isInitialLoadRef.current) {
      return
    }

    if (disabled) {
      return
    }

    if (hasPreferenceRequestFinished) {
      isInitialLoadRef.current = true

      const cleanedPrimary = preferences ? pickBy(preferences, (value) => value != null) : {}
      const cleanedFallback = fallbackPreferences ? pickBy(fallbackPreferences, (value) => value != null) : {}
      const hasPrimaryData = Object.keys(cleanedPrimary).length > 0
      const hasFallbackData = Object.keys(cleanedFallback).length > 0
      const cleanedPreferences = hasPrimaryData ? cleanedPrimary : (hasFallbackData ? cleanedFallback : cleanedPrimary)
      let preferencesToApply =
        omitLoadedKeys && omitLoadedKeys.length > 0
          ? omit(cleanedPreferences, omitLoadedKeys)
          : cleanedPreferences
      if (
        !hasPrimaryData
        && hasFallbackData
        && omitFallbackKeys
        && omitFallbackKeys.length > 0
      ) {
        preferencesToApply = omit(preferencesToApply, omitFallbackKeys)
      }

      if (setPreferenceValidationOptions) {
        // Tag the subType inside the callback itself (not just before handing it out): the caller
        // may invoke this async (e.g. after a validation network call), and tagging here keeps the
        // ref update atomic with the state update, so there's no window where the tag claims a
        // subType the actual validationResult doesn't correspond to yet.
        const requestSubType = subType
        setPreferenceValidationOptions({
          preferences: preferencesToApply,
          callback: (result) => {
            validationResultSubTypeRef.current = requestSubType
            setValidationResult(result)
          },
        })
      } else {
        if (Object.keys(preferencesToApply).length > 0) {
          dispatch(setPreferencesAction(preferencesToApply))
        }
      }
    }
  }, [
    subType,
    disabled,
    hasPreferenceRequestFinished,
    preferences,
    fallbackPreferences,
    setPreferenceValidationOptions,
    setValidationResult,
    setPreferencesAction,
    dispatch,
    omitLoadedKeys,
    omitFallbackKeys,
  ])

  useLayoutEffect(() => {
    if (disabled) {
      return
    }
    // A subType switch doesn't clear `validationResult` state itself (only lastValidationResultRef,
    // above), so a result still belonging to the OLD subType must not be (re)applied here just
    // because it now looks "new" relative to the reset ref — only act on a result actually tagged
    // for the CURRENTLY active subType.
    if (validationResultSubTypeRef.current !== subType) {
      return
    }
    if (validationResult !== null && validationResult !== lastValidationResultRef.current) {
      lastValidationResultRef.current = validationResult
      // skipSave suppresses only the WRITE — the corrected value still applies to the slice (the
      // workspace writer persists it in instance mode).
      if (validationResult.isCorrected && !skipSave) {
        pendingSaveRef.current = validationResult.preferences
        debouncedSave(validationResult.preferences)
      }
      dispatch(setPreferencesAction(validationResult.preferences))
    }
  }, [subType, disabled, skipSave, validationResult, debouncedSave, setPreferencesAction, dispatch])

  useEffect(() => {
    // skipSave (instance mode) suppresses the per-subtype write entirely — the workspace writer
    // owns persistence. Load/seed and isPreferencesLoaded are unaffected (handled above). Drop a
    // save already queued in the debounce right away rather than waiting for its own ref check to
    // no-op it on the timer — no point letting a doomed network call sit queued for debounceTime.
    if (disabled || skipSave) {
      debouncedSave.cancel()
      pendingSaveRef.current = null
      return
    }
    if (!isPreferencesLoaded || !state) {
      return
    }

    const currentPreferences = omitBy(pick(state, preferenceFields), isNil)

    if (isInitialSaveRef.current) {
      previousPreferencesRef.current = currentPreferences
      isInitialSaveRef.current = false
      return
    }

    if (!isEqual(previousPreferencesRef.current, currentPreferences)) {
      previousPreferencesRef.current = currentPreferences
      if (Object.keys(currentPreferences).length > 0) {
        pendingSaveRef.current = currentPreferences
        debouncedSave(currentPreferences)
      }
    }
  }, [state, isPreferencesLoaded, debouncedSave, preferenceFields, disabled, skipSave])

  // Hide-flush: a page refresh/close doesn't reliably run React's unmount cleanup before tearing
  // down, so a change still sitting in the debounce above would otherwise be silently lost. On
  // `visibilitychange -> hidden` send it immediately via a keepalive request (survives the document
  // unloading). skipSave (instance mode) also suppresses this — the workspace writer already owns
  // hide-flushing there.
  useEffect(() => {
    if (disabled || skipSave) {
      return undefined
    }
    const onVisibilityChange = () => {
      if (
        document.visibilityState !== "hidden"
        || pendingSaveRef.current === null
        || disabledRef.current
        || skipSaveRef.current
      ) {
        return
      }
      const pendingPreferences = pendingSaveRef.current
      debouncedSave.cancel()
      pendingSaveRef.current = null
      savePreferencesKeepalive(pendingPreferences)
    }
    document.addEventListener("visibilitychange", onVisibilityChange)
    return () => document.removeEventListener("visibilitychange", onVisibilityChange)
  }, [disabled, skipSave, debouncedSave, savePreferencesKeepalive])

  // On unmount, FLUSH (not cancel) any pending save: in-app navigation (e.g. switching dashboards)
  // unmounts without firing visibilitychange, so a change still inside the debounce window would
  // otherwise be dropped the same way the hide-flush above guards against. flush() no-ops when
  // nothing is pending. But if disabled/skipSave is true at teardown, drop it instead — this mode
  // is meant to fully suppress the write (the workspace writer owns persistence in skipSave's case).
  useEffect(() => () => {
    if (disabledRef.current || skipSaveRef.current) {
      debouncedSave.cancel()
      return
    }
    debouncedSave.flush()
  }, [debouncedSave])

  return isPreferencesLoaded
}
